import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { fmtDateTime } from "../components/format";
import type { AppSettings, DictItem } from "../api/types";

// Часовые пояса России (IANA); по умолчанию — московское время.
const TIMEZONES: [string, string][] = [
  ["Europe/Kaliningrad", "Калининград (UTC+2)"],
  ["Europe/Moscow", "Москва, Донецк (UTC+3)"],
  ["Europe/Samara", "Самара (UTC+4)"],
  ["Asia/Yekaterinburg", "Екатеринбург (UTC+5)"],
  ["Asia/Omsk", "Омск (UTC+6)"],
  ["Asia/Krasnoyarsk", "Красноярск (UTC+7)"],
  ["Asia/Irkutsk", "Иркутск (UTC+8)"],
  ["Asia/Yakutsk", "Якутск (UTC+9)"],
  ["Asia/Vladivostok", "Владивосток (UTC+10)"],
  ["Asia/Magadan", "Магадан (UTC+11)"],
  ["Asia/Kamchatka", "Камчатка (UTC+12)"],
];

type Tab = "incident_categories" | "species" | "miss_reasons" | "settings";

const TABS: [Tab, string][] = [
  ["incident_categories", "Категории инцидентов"],
  ["species", "Виды"],
  ["miss_reasons", "Причины непрохождения"],
  ["settings", "Настройки приложения"],
];

/**
 * Раздел «Справочники». Записи не удаляются, а выключаются: на них
 * ссылаются принятые события. Телефоны получают изменения при
 * ближайшей синхронизации.
 */
export function ReferencePage() {
  const { me } = useAuth();
  // Научный сотрудник ведёт только справочник видов.
  const tabs = me?.can_manage_reference ? TABS : TABS.filter(([id]) => id === "species");
  const [tab, setTab] = useState<Tab>(tabs[0]?.[0] ?? "species");

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Справочники</h2>
          <p className="muted small">Телефоны получают изменения при ближайшей синхронизации</p>
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="filters">
          <div className="segmented" role="group" aria-label="Справочник">
            {tabs.map(([id, title]) => (
              <button key={id} aria-pressed={tab === id} onClick={() => setTab(id)}>{title}</button>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" ? <SettingsForm /> : <Dictionary key={tab} kind={tab} />}
    </>
  );
}

function Dictionary({ kind }: { kind: Exclude<Tab, "settings"> }) {
  const species = kind === "species";
  const list = useQuery<{ items: DictItem[] }>(`/api/v1/dictionaries/${kind}`);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const items = list.data?.items ?? [];
  const current = items.find((i) => i.code === selected) ?? null;
  const blank: DictItem = {
    code: "", name: "", is_active: true, latin_name: "", is_protected: false,
    sort_order: (Math.max(0, ...items.map((i) => i.sort_order ?? 0)) + 10),
  };

  return (
    <>
      {list.error && <div className="error" role="alert">{list.error}</div>}
      {list.loading && !list.data && <p className="notice">Загрузка…</p>}

      <div className="split">
        <div className="split-list">
          <button style={{ marginBottom: 12 }} onClick={() => { setCreating(true); setSelected(null); }}>
            {species ? "Новый вид" : "Новая запись"}
          </button>
          <div className="list">
            {list.data && items.length === 0 && <div className="empty">Справочник пуст</div>}
            {items.map((i) => (
              <button key={i.code} className={`item${i.code === selected && !creating ? " selected" : ""}`}
                onClick={() => { setSelected(i.code); setCreating(false); }}>
                <span className="item-body">
                  <span className="item-head">
                    <span className="item-title">{i.name}</span>
                    {i.is_protected && <span className="badge warn">охраняемый</span>}
                    {!i.is_active && <span className="badge">выключено</span>}
                  </span>
                  {species && i.latin_name && <span className="item-text"><i>{i.latin_name}</i></span>}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="split-detail">
          {(creating || current) ? (
            <ItemForm
              key={creating ? "new" : current!.code}
              kind={kind}
              initial={creating ? blank : current!}
              onDone={(code) => { list.reload(); setCreating(false); setSelected(code); }}
              onCancel={creating ? () => setCreating(false) : undefined}
            />
          ) : (
            <div className="card empty">Выберите запись или заведите новую</div>
          )}
        </div>
      </div>
    </>
  );
}

function ItemForm({ kind, initial, onDone, onCancel }: {
  kind: Exclude<Tab, "settings">;
  initial: DictItem;
  onDone: (code: string) => void;
  onCancel?: () => void;
}) {
  const species = kind === "species";
  const [form, setForm] = useState<DictItem>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = JSON.stringify(form) !== JSON.stringify(initial);
  // «Вид не определён» подставляется по умолчанию — выключать его нельзя.
  const fixed = species && form.code === "unknown";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Сервер отвергает лишние поля, поэтому у видов и простых справочников тело своё.
      const { code, name, is_active, sort_order, latin_name, is_protected } = form;
      const body = species ? { code, name, is_active, latin_name, is_protected } : { code, name, is_active, sort_order };
      const res = await api.post<{ code: string }>(`/api/v1/dictionaries/${kind}`, body);
      onDone(res.code);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>{initial.code ? initial.name : species ? "Новый вид" : "Новая запись"}</h3>
      <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <div className="error" role="alert">{error}</div>}
        <label className="field">Название
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        {species ? (
          <>
            <label className="field">Латинское название (необязательно)
              <input value={form.latin_name ?? ""} onChange={(e) => setForm({ ...form, latin_name: e.target.value })} />
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
              <input type="checkbox" checked={form.is_protected ?? false} style={{ minHeight: 0 }}
                onChange={(e) => setForm({ ...form, is_protected: e.target.checked })} />
              Охраняемый — точные координаты видят только научный отдел и директор
            </label>
          </>
        ) : (
          <label className="field">Порядок в списке
            <input type="number" value={form.sort_order ?? 0}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          </label>
        )}
        {!fixed && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
            <input type="checkbox" checked={form.is_active} style={{ minHeight: 0 }}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Действует — выключенная запись не предлагается в приложении, но остаётся в принятых событиях
          </label>
        )}
        <div className="filters" style={{ margin: 0 }}>
          <button type="submit" disabled={busy || !changed}>{initial.code ? "Сохранить" : "Завести"}</button>
          {onCancel && <button type="button" className="secondary" onClick={onCancel}>Отмена</button>}
        </div>
      </form>
    </section>
  );
}

function SettingsForm() {
  const q = useQuery<AppSettings>("/api/v1/settings");
  if (q.error) return <div className="error" role="alert">{q.error}</div>;
  if (!q.data) return <p className="notice">Загрузка…</p>;
  return <SettingsFields key={q.data.updated_at} initial={q.data} onSaved={q.reload} />;
}

function SettingsFields({ initial, onSaved }: { initial: AppSettings; onSaved: () => void }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = JSON.stringify(form) !== JSON.stringify(initial);
  const num = (key: keyof AppSettings) => ({
    type: "number" as const,
    required: true,
    value: form[key] as number,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: Number(e.target.value) }),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.put("/api/v1/settings", form);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" style={{ maxWidth: 560 }}>
      <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <div className="error" role="alert">{error}</div>}
        <h3>GPS-трек</h3>
        <label className="field">Интервал фиксации, секунд (5–300)
          <input min={5} max={300} {...num("track_interval_s")} />
        </label>
        <label className="field">Минимальное смещение, метров (0–500)
          <input min={0} max={500} {...num("track_distance_m")} />
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          Точка пишется, когда прошло не меньше интервала и инспектор сместился не меньше чем на заданное
          расстояние. Частая фиксация быстрее расходует батарею за смену; редкая хуже подтверждает прохождение маршрута.
          Новые параметры применяются со следующего обхода.
        </p>
        <h3>Синхронизация</h3>
        <label className="field">Период фоновой отправки, минут (15–240)
          <input min={15} max={240} {...num("sync_interval_min")} />
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          Страховочная отправка по расписанию. Новые события уходят сразу при наличии сети, независимо от периода.
        </p>
        <h3>Защита приложения</h3>
        <label className="field">Блокировка после бездействия, минут (1–120)
          <input min={1} max={120} {...num("lock_after_min")} />
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          Если приложение было свёрнуто дольше этого срока, при возврате оно спросит PIN-код.
          Запись маршрута в фоне при этом не прерывается.
        </p>
        <label className="field">Вход без связи с сервером, дней (1–30)
          <input min={1} max={30} {...num("offline_access_days")} />
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          Столько дней после последней синхронизации инспектор входит по PIN-коду или жетону без сети. Когда срок
          истёк, вход открывается только при связи: так блокировка устройства или жетона в панели гарантированно
          доходит до телефона. Запись маршрута и очередь при этом не теряются.
        </p>
        <h3>Время</h3>
        <label className="field">Часовой пояс панели и отчётов
          <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
            {TIMEZONES.map(([tz, title]) => <option key={tz} value={tz}>{title}</option>)}
          </select>
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          Сервер хранит время в UTC; здесь задаётся, в каком поясе его показывают панель, выгрузки и PDF.
          Новый пояс применяется к панели при следующем входе.
        </p>
        <div className="filters" style={{ margin: 0, alignItems: "center" }}>
          <button type="submit" disabled={busy || !changed}>Сохранить</button>
          <span className="muted small">Изменено {fmtDateTime(initial.updated_at)}</span>
        </div>
      </form>
    </section>
  );
}
